import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { MultiSelectDropDown } from 'src/components/common/multiselect-dropdown';
import { issueApi } from 'src/api/issue';
import { intersectionArray } from 'src/components/masters/customFunctions';
import { IssueModelShort } from 'src/interfaces/masters/issue/issue';
import { InputField } from 'src/components/input-field';
import { ResponseCodes } from 'src/enums/responseCodes';
import toast from 'react-hot-toast';
import loadingContext from 'src/loderContext';

interface AddUserIssuesProps {
  open: boolean;
  productId: string;
  onClose: () => void;
  issuesDetailData: string[];
  setIssuesData?: (data: string[]) => void;
}

export const AddUserIssues: FC<AddUserIssuesProps> = (props) => {
  const { open, onClose, productId, setIssuesData, issuesDetailData } = props;
  const { setLoading } = useContext(loadingContext);
  const [issuesDetail, setIssuesDetail] = useState<IssueModelShort[]>([]);
  const [otherIssue, setOtherIssue] = useState<string>('');
  const [filterIssues, setFilterIssues] = useState<any[]>([]);
  const [selectedIssues, setSelectedIssues] = useState<any[]>([]);
  const [issueDescription, setIssueDescription] = useState<string[]>([]);
  const [isAvailable, setIsAvailable] = useState<string>('Loading');

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      if (issuesDetailData?.length > 1) {
        const intersectedValue = intersectionArray(issuesDetail, issuesDetailData, 'description');
        const tempDescriptionData : string[] = [];
        intersectedValue?.map((item) => {
          tempDescriptionData.push(item.name);
          return null;
        });
        setIssueDescription(tempDescriptionData);
        setSelectedIssues(intersectedValue);
      } else if (issuesDetailData?.length === 1 || issuesDetail?.length === 0) {
        if (otherIssue === '' && issuesDetail.length === 1) {
          setIssuesData([issuesDetailData[0]]);
        }
      } else {
        setIssueDescription([]);
        setSelectedIssues([]);
      }
      onClose();
    }
  };

  const handleDelete = (item) => {
    const newIssuesList = selectedIssues.filter((Issue) => Issue.id !== item.id);
    const newIssuesDescription = issueDescription.filter(
      (Issue) => Issue !== item.name
    );
    setSelectedIssues([...newIssuesList]);
    setIssueDescription([...newIssuesDescription]);
  };

  const getIssuesDetails = async () => {
    setLoading(true);
    const result = await issueApi.getIssuesForProduct(productId);
    if (result?.statusCode === ResponseCodes.OK) {
      setIssuesDetail(result?.responseData);
      setFilterIssues(result?.responseData?.map((issue) => ({
        ...issue,
        id: issue.issueId,
        value: issue.description,
      })));
      if (issuesDetailData.length) {
        const intersectedValue = intersectionArray(result?.responseData, issuesDetailData, 'description');
        setSelectedIssues(intersectedValue?.map((issue) => ({
          ...issue,
          id: issue.issueId,
          name: issue.description,
        })));
        setIssueDescription(issuesDetailData);
      }
      setIsAvailable(result?.responseData?.length !== 0 ? 'Success' : 'Fail');
      setOtherIssue((result?.responseData?.length === 0 && issuesDetailData.length === 1) ? issuesDetailData[0] : '');
    } else if (result?.statusCode === ResponseCodes.NOT_FOUND) {
      setIsAvailable('Fail');
      setOtherIssue(issuesDetailData[0] || '');
      toast.error(result?.responseData?.toString());
    } else {
      toast.error(result?.responseData?.toString());
    }
    setLoading(false);
  };

  useEffect(() => {
    getIssuesDetails().catch(console.error);
  }, []);

  // Handles multi select dropdown selection list
  const multiSelectionHandler = (event) => {
    // Selected id from dropdown
    const selectedId = event.target.value[event.target.value.length - 1]
      ? event.target.value[event.target.value.length - 1].trim()
      : null;
    const selected = issuesDetail?.filter((issue) => issue.issueId === selectedId)[0];
    const issueExist = selectedIssues.filter(
      (issue) => issue.id === selectedId
    ).length > 0;
    if (!issueExist) {
      setSelectedIssues(
        [...selectedIssues, { name: selected.description, id: selected.issueId }]
      );
      setIssueDescription([...issueDescription, selected.description]);
    } else {
      const newItems = selectedIssues.filter((issue) => issue.id !== selectedId);
      setSelectedIssues(newItems);
      setIssueDescription(newItems.map((item) => item.name));
    }
  };

  const addIssues = async (e) => {
    setIssuesData(issueDescription);
    closeDialog(e, '');
  };

  return (
    <Dialog
      fullWidth
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        Issues
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      { isAvailable === 'Loading' && (
        <>
          <Typography
            sx={{
              ml: 4,
              mb: 4
            }}
            variant="body2"
          >
            Loading...
          </Typography>
        </>
      )}
      { isAvailable === 'Fail' && (
      <>
        <InputField
          multiline
          sx={{
            px: 4,
            mb: 4
          }}
          fullWidth
          required
          name="otherIssue"
          onChange={(e) => {
            setOtherIssue(e.target.value);
          }}
          label="Add Issue"
          placeholder="Enter Issue"
          value={otherIssue}
        />
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            pb: 3
          }}
        >
          <Box sx={{ flexGrow: 1 }} />
          <Button
            sx={{
              px: 4,
              mr: 4
            }}
            disabled={otherIssue === '' || (issuesDetailData[0] === otherIssue)}
            color="primary"
            onClick={(e) => {
              setIssuesData([otherIssue]);
              closeDialog(e, '');
            }}
            variant="contained"
          >
            {issuesDetailData.length > 0 ? 'Update' : 'Add'}
          </Button>
        </Box>
      </>
      )}
      { isAvailable === 'Success' && (
      <>
        <DialogContent>
          <Grid
            container
            spacing={4}
            paddingY={4}
          >
            <Grid
              item
              xs={12}
            >
              <MultiSelectDropDown
                label="Select Issues"
                index="1"
                options={filterIssues}
                onSelect={multiSelectionHandler}
                selectedOptions={[...issueDescription]}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              {selectedIssues?.length > 0 ? (
                <Box>
                  {selectedIssues.map((item) => (
                    <Chip
                      color="primary"
                      variant="outlined"
                      key={item.id}
                      sx={{ mr: 2, mb: 1, borderRadius: '20px', m: 0.5 }}
                      label={item.name}
                      tabIndex={-1}
                      onDelete={() => {
                        handleDelete(item);
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    pt: 5,
                    pb: 5,
                    textAlign: 'center',
                    border: '.5px solid',
                  }}
                >
                  <Typography
                    color="textPrimary"
                    variant="caption"
                  >
                    No Issues Added
                  </Typography>
                </Box>
              )}

            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'right'
          }}
        >
          <Button
            color="primary"
            onClick={(e) => {
              closeDialog(e, '');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={(issuesDetailData?.every((val) => issueDescription?.includes(val))
                && issuesDetailData.length === issueDescription?.length)
                || (issueDescription?.length < 1)}
            color="primary"
            onClick={(e) => {
              addIssues(e);
            }}
            variant="contained"
          >
            {issuesDetailData.length > 0 ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </>
      )}
    </Dialog>
  );
};

AddUserIssues.defaultProps = {
  open: false,
};

AddUserIssues.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  productId: PropTypes.string,
  // @ts-ignore
  issuesDetailData: PropTypes.object,
  setIssuesData: PropTypes.func
};
